import Sound from './Sound';
import util from './util';

const ERROR_RATE = 0.01;

function randomBetween(min, max) {
	return min + Math.random() * (max - min);
}

function abortError() {
	return new DOMException('The operation was aborted.', 'AbortError');
}

// Waits ms, rejecting early with an AbortError if signal aborts first.
function sleep(ms, signal) {
	return new Promise((resolve, reject) => {
		if(signal?.aborted) return reject(abortError());
		const timer = setTimeout(resolve, ms);
		signal?.addEventListener('abort', () => {
			clearTimeout(timer);
			reject(abortError());
		}, { once: true });
	});
}

// Stand-in for PcmSound/UrlSound/MediaSound in preview contexts with no real audio
// backend. Mimics their readyState/event contract, including occasional failures
// (~10% of loads and plays) so error-handling paths get exercised in previews too.
export default class MockSound extends Sound {
	#stopAc = null;

	setPlaybackRate() {}

	load(signal) {
		return this.loadPromise ||= (async () => {
			this._setReadyState('loading');
			try {
				await sleep(randomBetween(300, 1200), signal);
			} catch(e) {
				this.loadPromise = null;
				this._setReadyState('error', e);
				throw e;
			}
			if(Math.random() < ERROR_RATE) {
				const e = new Error('Mock sound failed to load');
				this.loadPromise = null;
				this._setReadyState('error', e);
				throw e;
			}
			this._setReadyState('ready');
		})();
	}

	play(signal) {
		this.#stopAc = new AbortController();
		const combined = signal ? util.signalAny([this.#stopAc.signal, signal]) : this.#stopAc.signal;

		return new Promise((resolve, reject) => {
			if(combined.aborted) return resolve();

			const onAbort = () => {
				clearTimeout(timer);
				resolve();
			};
			combined.addEventListener('abort', onAbort, { once: true });

			this._setReadyState('playing', { target: this });
			const timer = setTimeout(() => {
				combined.removeEventListener('abort', onAbort);
				if(Math.random() < ERROR_RATE) {
					const e = new Error('Mock sound playback failed');
					this._setReadyState('error', { target: this, type: 'playbackError', detail: e, message: 'Playback error' });
					reject(e);
				} else {
					this._setReadyState('ended', { target: this });
					resolve();
				}
			}, randomBetween(800, 3000));
		});
	}

	stop() {
		this.#stopAc?.abort();
	}
}
